/**
 * Tenant lifecycle: whether a customer is still a customer.
 *
 * `/v2/vendor/tenants/{id}/status` is its own surface rather than a field on the
 * company PATCH, because the decision belongs to the vendor: the company
 * endpoints are what a tenant's own console writes, and this is the one fact
 * about a tenant that only we may change.
 *
 * Disabling stops every write the tenant can make and leaves every read
 * working, so a disabled customer keeps its documents and can still take them
 * with it.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPut } from '../utils/request'

export interface TenantStatus {
  tenant_id: number
  /** When it was disabled, or null while it is a customer. Does not move on a repeat. */
  disabled_utc?: string | null
  disabled_reason?: string | null
  disabled_by_user_email?: string | null
}

export interface TenantStatusChange {
  tenantId: number
  disabled: boolean
  /** Why, in the vendor's own words. The API ignores it when re-enabling. */
  reason?: string | null
}

/**
 * Query hook to fetch a tenant's status
 */
export const useTenantStatusQuery = (
  tenantId: MaybeRefOrGetter<number | null | undefined>,
) => {
  return useQuery({
    queryKey: computed(() => {
      const id = toValue(tenantId)
      return id ? queryKeys.tenantStatus(id) : ['tenants', 'status', 'null']
    }),
    queryFn: async (): Promise<TenantStatus> => {
      const id = toValue(tenantId)
      if (!id) throw new Error('Tenant ID is required')
      return queryGet<TenantStatus>(`/v2/vendor/tenants/${id}/status`)
    },
    enabled: computed(() => !!toValue(tenantId)),
  })
}

/**
 * Mutation hook to disable a tenant, or to take it back
 */
export const useSetTenantStatusMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      tenantId,
      disabled,
      reason,
    }: TenantStatusChange): Promise<TenantStatus> => {
      return queryPut<TenantStatus>(`/v2/vendor/tenants/${tenantId}/status`, {
        disabled,
        reason: reason || null,
      })
    },
    onSuccess: (status) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantStatus(status.tenant_id) })
      // The tenant list reads `disabled_utc` per row and can be filtered by it,
      // so a change here moves which rows a filtered list should hold.
      queryClient.invalidateQueries({ queryKey: queryKeys.companies() })
    },
  })
}
